import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Bot, User } from "lucide-react";
import { chatbotSuggestions, company } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ensureSessionId() {
  let sid = localStorage.getItem("chat_session_id");
  if (!sid) {
    sid = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("chat_session_id", sid);
  }
  return sid;
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [sessionId] = useState(() => ensureSessionId());
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: `Hey, I'm 411 Bot \u{1F44B} Ask me anything about Eddie's services, pricing or how to get started.`,
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing, open]);

  const send = async (txt) => {
    const message = (txt ?? input).trim();
    if (!message || typing) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setInput("");
    setTyping(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message,
      }, { timeout: 60000 });
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          text: `Sorry, I'm having trouble connecting right now. Please call Eddie directly at ${company.phone} or use the contact form below.`,
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white grid place-items-center shadow-2xl hover:scale-105"
        aria-label="Open chat"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && (
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-400 border-2 border-white" />
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[540px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden fade-up">
          <div className="bg-slate-900 text-white px-5 py-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-cyan-500 grid place-items-center">
              <Bot className="h-5 w-5 text-slate-900" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm">411 Bot</div>
              <div className="text-xs text-slate-300 flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                AI assistant  Usually replies in seconds
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "bot" && (
                  <div className="h-7 w-7 rounded-full bg-cyan-600 text-white grid place-items-center flex-shrink-0">
                    <Bot className="h-3.5 w-3.5" />
                  </div>
                )}
                <div
                  className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-slate-900 text-white rounded-br-sm"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
                {m.role === "user" && (
                  <div className="h-7 w-7 rounded-full bg-slate-200 text-slate-700 grid place-items-center flex-shrink-0">
                    <User className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))}
            {typing && (
              <div className="flex gap-2 justify-start">
                <div className="h-7 w-7 rounded-full bg-cyan-600 text-white grid place-items-center flex-shrink-0">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-2.5">
                  <span className="dot" /><span className="dot" /><span className="dot" />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 bg-slate-50 border-t border-slate-100">
              {chatbotSuggestions.slice(0, 3).map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-full hover:border-cyan-500 hover:text-cyan-700"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 bg-white border-t border-slate-200 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
            <button
              type="submit"
              disabled={typing}
              className="h-10 w-10 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white grid place-items-center"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
          <div className="px-4 py-2 text-[10px] text-slate-400 text-center border-t border-slate-100 bg-white">
            Prefer to talk? Call Eddie at {company.phone}
          </div>
        </div>
      )}
    </>
  );
}
